import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Building2, Save, Landmark } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const CompanyInfoSection = () => {
  const [info, setInfo] = useState({
    company_name: '', company_registration: '', company_address: '', company_phone: '', company_email: '',
    bank_name: '', bank_account_number: '', bank_account_holder: '', bank_swift_code: '',
    epf_employer_rate: 13, epf_employee_rate: 11, socso_note: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [qrPreviewUrl, setQrPreviewUrl] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/api/company-info`, { withCredentials: true });
        if (data && Object.keys(data).length) setInfo((prev) => ({ ...prev, ...data }));
        if (data && data.bank_qr_storage_path) {
          try {
            const resp = await axios.get(`${API}/api/company-info/qr`, { responseType: 'blob', withCredentials: true });
            const url = URL.createObjectURL(resp.data);
            setQrPreviewUrl(url);
          } catch (e) { /* ignore preview errors */ }
        }
      } catch (e) { /* silent */ } finally { setLoading(false); }
    })();
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        ...info,
        epf_employer_rate: parseFloat(info.epf_employer_rate) || 13,
        epf_employee_rate: parseFloat(info.epf_employee_rate) || 11,
      };
      await axios.put(`${API}/api/company-info`, payload, { withCredentials: true });
      toast.success('Company info saved');
    } catch (e) { toast.error('Failed to save'); } finally { setSaving(false); }
  };

  const handleQrUpload = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    try {
      const { data } = await axios.post(`${API}/api/company-info/qr`, fd, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setInfo((p) => ({ ...p, ...data }));
      // fetch preview
      const resp = await axios.get(`${API}/api/company-info/qr`, { responseType: 'blob', withCredentials: true });
      const url = URL.createObjectURL(resp.data);
      setQrPreviewUrl(url);
      toast.success('QR uploaded');
    } catch (err) {
      console.error(err);
      toast.error('Failed to upload QR');
    }
  };

  const upd = (k, v) => setInfo({ ...info, [k]: v });

  if (loading) return <div className="flex justify-center py-12"><div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div></div>;

  return (
    <div className="space-y-6" data-testid="company-info-section">
      <Card className="bg-white border-slate-200">
        <CardHeader>
          <CardTitle className="text-xl font-medium tracking-tight flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            <Building2 className="w-5 h-5 text-blue-600" />
            Company / School Information
          </CardTitle>
          <CardDescription>Displayed on payslips and payment receipts</CardDescription>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div><Label>Company / School Name</Label><Input value={info.company_name} onChange={(e) => upd('company_name', e.target.value)} placeholder="e.g., SSTC Special Needs School" data-testid="company-name-input" /></div>
          <div><Label>Registration No. (SSM)</Label><Input value={info.company_registration} onChange={(e) => upd('company_registration', e.target.value)} placeholder="e.g., 202301012345" data-testid="company-reg-input" /></div>
          <div className="md:col-span-2"><Label>Address</Label><Textarea value={info.company_address} onChange={(e) => upd('company_address', e.target.value)} rows={2} data-testid="company-address-input" /></div>
          <div><Label>Phone</Label><Input value={info.company_phone} onChange={(e) => upd('company_phone', e.target.value)} data-testid="company-phone-input" /></div>
          <div><Label>Email</Label><Input type="email" value={info.company_email} onChange={(e) => upd('company_email', e.target.value)} data-testid="company-email-input" /></div>
        </CardContent>
      </Card>

      <Card className="bg-white border-slate-200">
        <CardHeader>
          <CardTitle className="text-xl font-medium tracking-tight flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            <Landmark className="w-5 h-5 text-blue-600" />
            Bank Account (for parent payments)
          </CardTitle>
          <CardDescription>Parents will transfer school fees to this account via FPX / bank transfer</CardDescription>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div><Label>Bank Name</Label><Input value={info.bank_name} onChange={(e) => upd('bank_name', e.target.value)} placeholder="e.g., Maybank" data-testid="bank-name-input" /></div>
          <div><Label>Account Number</Label><Input value={info.bank_account_number} onChange={(e) => upd('bank_account_number', e.target.value)} placeholder="e.g., 5555-1234-5678" data-testid="bank-account-input" /></div>
          <div><Label>Account Holder</Label><Input value={info.bank_account_holder} onChange={(e) => upd('bank_account_holder', e.target.value)} data-testid="bank-holder-input" /></div>
          <div><Label>SWIFT Code (optional)</Label><Input value={info.bank_swift_code} onChange={(e) => upd('bank_swift_code', e.target.value)} data-testid="bank-swift-input" /></div>
          <div className="md:col-span-2">
            <Label>Bank QR Code (upload)</Label>
            <input type="file" accept="image/*" onChange={handleQrUpload} className="mt-2" data-testid="bank-qr-input" />
            {qrPreviewUrl && (
              <div className="mt-2">
                <img src={qrPreviewUrl} alt="Bank QR" style={{ maxWidth: 200, maxHeight: 200 }} />
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <Card className="bg-white border-slate-200">
        <CardHeader>
          <CardTitle className="text-xl font-medium tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>Payroll Settings</CardTitle>
          <CardDescription>Used to auto-calculate EPF on payslips (Malaysian statutory rates)</CardDescription>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div><Label>EPF Employee Rate (%)</Label><Input type="number" step="0.1" value={info.epf_employee_rate} onChange={(e) => upd('epf_employee_rate', e.target.value)} data-testid="epf-ee-rate-input" /></div>
          <div><Label>EPF Employer Rate (%)</Label><Input type="number" step="0.1" value={info.epf_employer_rate} onChange={(e) => upd('epf_employer_rate', e.target.value)} data-testid="epf-er-rate-input" /></div>
          <div className="md:col-span-2"><Label>Notes for Payslips</Label><Textarea value={info.socso_note} onChange={(e) => upd('socso_note', e.target.value)} rows={2} data-testid="payroll-note-input" /></div>
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button className="bg-blue-600 hover:bg-blue-700" onClick={save} disabled={saving} data-testid="save-company-info-button">
          <Save className="w-4 h-4 mr-2" /> {saving ? 'Saving...' : 'Save Company Info'}
        </Button>
      </div>
    </div>
  );
};

export default CompanyInfoSection;
